import { tieneAccesoTotal } from "@/utils/roles";
import axios from "axios";
import toastr from "toastr";

const AUTH_FAIL_STATUSES = [401, 419, 409];

const clearAuthStorage = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("propiedadActiva");
  localStorage.removeItem("selectPropertyView");
  localStorage.removeItem("type");
  localStorage.removeItem("user");
  localStorage.removeItem("legacy_redirect");
  localStorage.removeItem("sub_user"); // identidad del sub-usuario (si aplica)
};

const clearAxiosAuthHeader = () => {
  delete axios.defaults.headers.common["Authorization"];
};

/**
 * Guarda la sesión: token, usuario y, si es un miembro del equipo, su
 * identidad y la propiedad sobre la que puede trabajar.
 *
 * Estaba dentro de `retrieveToken`. Se saca aquí porque ahora hay DOS caminos
 * que acaban en una sesión —entrar con contraseña, y entrar con el código del
 * correo— y tenerlo escrito dos veces era garantizar que un día se separasen.
 */
const guardarSesion = (context, payload) => {
  // ✅ Caso normal (backend NUEVO): guarda token y user
  const token = payload.token;
  axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  localStorage.setItem("access_token", token);
  localStorage.setItem("user", JSON.stringify(payload.user));
  localStorage.setItem("type", payload.user?.type || "");

  // Sub-usuario: guarda su identidad separada para UI
  if (payload.user?.sub_user) {
    const subUser = payload.user.sub_user;
    localStorage.setItem("sub_user", JSON.stringify(subUser));

    // Todo miembro restringido debe operar únicamente sobre una propiedad
    // asignada. Anfitrión (y el antiguo gestor) conservan alcance total.
    const assigned = Array.isArray(subUser.assigned_property_ids)
      ? subUser.assigned_property_ids.map(Number)
      : [];

    const fullAccess = tieneAccesoTotal(subUser);
    if (!fullAccess && assigned.length > 0) {
      const storedActive = Number(
        localStorage.getItem("propiedadActiva") || 0
      );

      if (!storedActive || !assigned.includes(storedActive)) {
        const firstAllowed = String(assigned[0]);
        localStorage.setItem("propiedadActiva", firstAllowed);
        localStorage.setItem("selectPropertyView", firstAllowed);
        context.state.propiedadActiva = assigned[0];
        context.state.selectPropertyView = assigned[0];
      }
    }
  } else {
    localStorage.removeItem("sub_user");
  }

  context.commit("retrieveToken", token);
};

const actions = {
  async errorServerRequest({ commit }, error) {
    const errors = error?.response?.data?.errors || error?.response?.data?.data;
    if (errors) {
      Object.values(errors)
        .flat()
        .forEach((msg) => toastr.error(msg));
      return;
    }
    toastr.error("Error al completar la solicitud");
  },

  async fetchUser({ commit, dispatch }) {
    try {
      const response = await axios.get("/user");
      commit("setUserGlobal", response.data);
      return response.data;
    } catch (error) {
      const status = error?.response?.status;
      if (AUTH_FAIL_STATUSES.includes(status)) {
        await dispatch("forceLogout");
        window.location.href = "/";
        return;
      }
      console.log(error);
      console.log(" - -- Error al cargar data user - -- - ");
    }
  },

  // ✅ NUEVO: logout forzado (no llama /logout)
  forceLogout(context) {
    clearAuthStorage();
    clearAxiosAuthHeader();
    context.commit("destroyToken");

    // si tienes más commits para limpiar user global, puedes añadir:
    // context.commit("setUserGlobal", null);
    // context.commit("user/SET_USER", null, { root: true });

    return true;
  },

  retrieveToken(context, credentials) {
    return axios
      .post(
        "/login",
        { email: credentials.email, password: credentials.password },
        { withCredentials: true }
      )
      .then((response) => {
        if (response?.data?.ok) {
          const payload = response.data.data || {};

          // Verificación en dos pasos: el servidor NO ha dado sesión todavía,
          // ha mandado un código al correo. Aquí no hay nada que guardar —si
          // se siguiera, se grabaría `undefined` como token— así que se
          // devuelve tal cual y la pantalla enseña el paso del código.
          if (payload.requiere_verificacion) {
            return response;
          }

          // 🔁 Caso USUARIO LEGACY: NO guardes token del backend nuevo, redirige.
          if (payload.legacy_redirect) {
            const legacyToken = payload.token;
            const legacyUser = payload.user || null;

            const u = legacyUser
              ? encodeURIComponent(
                  btoa(unescape(encodeURIComponent(JSON.stringify(legacyUser))))
                )
              : "";

            const bridge = `https://app.wacheckin.com/legacy-bridge.html#t=${encodeURIComponent(
              legacyToken
            )}&u=${u}`;

            localStorage.setItem("legacy_redirect", "1");
            if (payload.message) toastr.info(payload.message);

            window.location.href = bridge;
            return response;
          }

          guardarSesion(context, payload);
        }

        return response;
      });
  },

  /**
   * Segundo paso: manda el código que llegó al correo.
   *
   * No se vuelve a mandar el correo ni la contraseña: el `desafio` que dio el
   * servidor es lo único que dice de quién es esta sesión a medias.
   */
  verificarCodigoAcceso(context, { desafio, codigo }) {
    return axios
      .post("/login/verify", { desafio, codigo }, { withCredentials: true })
      .then((response) => {
        if (response?.data?.ok) {
          guardarSesion(context, response.data.data || {});
        }
        return response;
      });
  },

  /**
   * Pide que reenvíen el código, sobre el mismo desafío: por correo o, si el
   * correo no llega, por WhatsApp. El idioma es el de la pantalla, para que el
   * mensaje de WhatsApp llegue en el suyo.
   */
  reenviarCodigoAcceso(context, { desafio, canal = "correo", idioma = "es" }) {
    return axios.post("/login/resend", { desafio, canal, idioma }, { withCredentials: true });
  },

  retrieveTokenProduction(context, credentials) {
    return axios
      .post(
        "/login",
        { email: credentials.email, password: credentials.password },
        { withCredentials: true }
      )
      .then((response) => {
        if (response?.data?.ok) {
          const token = response.data.data.token;
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
          localStorage.setItem("access_token", token);
          localStorage.setItem("user", JSON.stringify(response.data.data.user));
          localStorage.setItem("type", response.data.data.user?.type || "");
          context.commit("retrieveToken", token);
        }

        return response;
      });
  },

  // ✅ Mejorado: siempre limpia aunque /logout falle o token esté revocado
  destroyToken(context) {
    return new Promise((resolve) => {
      axios
        .post("/logout")
        .catch(() => {
          // ignoramos el error (token revocado / ya expiró / etc.)
        })
        .finally(() => {
          clearAuthStorage();
          clearAxiosAuthHeader();
          context.commit("destroyToken");
          resolve(true);
        });
    });
  },
};

export default actions;
